import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * SAB-987: last season's line, the view the NFL draft room never had.
 *
 * ── WHY LAST SEASON IS HARDCODED HERE AND NOWHERE ELSE ──────────────────────
 *
 * SAB-824 removed the last bare `2025` literal from the client, because a 2026 league was
 * silently rendering last season's game log as if it were current. That fix was right and
 * this is not a regression of it: the number a drafter wants IS last season, the panel says
 * so in its own heading, and it sits beside the current-season log rather than replacing it.
 * The distinction that mattered in SAB-824 was "unlabelled and mistaken for now"; this is
 * labelled and cannot be.
 *
 * ── SOURCE ──────────────────────────────────────────────────────────────────
 *
 * get_nfl_player_season_line aggregates nfl_player_game_stats over 2025 REG. NOT
 * nfl_player_season_stats, which the ticket names: that table has no sabotage/trash/anti
 * columns at all, and covers 830 of the 2292 listed players against game stats' 1965.
 *
 * ── POSITION-SHAPED, NOT ONE WIDE ROW ───────────────────────────────────────
 *
 * A QB's line and a linebacker's line share no columns. Rendering every stat for everyone
 * would put eighteen mostly-zero numbers in front of a drafter with seconds on the clock,
 * which is worse than showing nothing. Each position family gets the line its own position
 * is judged on, and rushing is appended for a QB or receiver only when it is non-zero --
 * a rushing quarterback is exactly the case a drafter wants surfaced.
 */
export interface SeasonLine {
  season_year: number; games_played: number;
  sabotage_points: number; trash_points: number; anti_points: number; avg_sabotage: number;
  pass_yds: number; pass_td: number; pass_int: number; pass_cmp: number; pass_att: number;
  rush_yds: number; rush_td: number; rush_att: number;
  rec: number; rec_yds: number; rec_td: number;
  tackles: number; sacks: number; def_int: number; fum_lost: number;
}

const IDP = new Set(['LB', 'DB', 'DL', 'DE', 'DT', 'CB', 'S', 'SS', 'FS', 'MLB', 'OLB', 'NT', 'SAF']);
const n = (v: unknown) => Number(v ?? 0);

/** The columns a drafter actually reads for this position. */
function conventional(position: string, s: SeasonLine): [string, string][] {
  const out: [string, string][] = [];
  const pos = (position || '').toUpperCase();
  if (pos === 'QB') {
    out.push(['YDS', n(s.pass_yds).toLocaleString()], ['TD', String(n(s.pass_td))], ['INT', String(n(s.pass_int))]);
    if (n(s.pass_att) > 0) out.push(['CMP', `${n(s.pass_cmp)}/${n(s.pass_att)}`]);
  } else if (pos === 'RB') {
    out.push(['RUSH', n(s.rush_yds).toLocaleString()], ['TD', String(n(s.rush_td))], ['REC', String(n(s.rec))], ['REC YDS', n(s.rec_yds).toLocaleString()]);
  } else if (pos === 'WR' || pos === 'TE') {
    out.push(['REC', String(n(s.rec))], ['YDS', n(s.rec_yds).toLocaleString()], ['TD', String(n(s.rec_td))]);
  } else if (IDP.has(pos)) {
    out.push(['TKL', String(n(s.tackles))], ['SACK', String(n(s.sacks))], ['INT', String(n(s.def_int))]);
  }
  // A rushing QB or receiver is the case worth surfacing; a zero is not.
  if ((pos === 'QB' || pos === 'WR' || pos === 'TE') && n(s.rush_yds) > 0) {
    out.push(['RUSH', n(s.rush_yds).toLocaleString()]);
  }
  return out;
}

export default function NFLSeasonLine({
  playerId, position, seasonYear = 2025, className,
}: { playerId: string; position: string; seasonYear?: number; className?: string }) {
  const [line, setLine] = useState<SeasonLine | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setFailed(false);
    (async () => {
      const { data, error } = await supabase.rpc('get_nfl_player_season_line' as never, {
        p_player_id: playerId, p_season_year: seasonYear,
      } as never);
      if (!active) return;
      // "No games" and "the call failed" must not look the same: the first is a fact about
      // the player, the second is a fact about us, and a drafter should be told which.
      if (error) { setFailed(true); setLine(null); } else { setLine(data as unknown as SeasonLine); }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [playerId, seasonYear]);

  if (loading) return <Skeleton className={cn('h-14 w-full', className)} />;
  if (failed) return <p className={cn('text-[11px] text-muted-foreground', className)}>Couldn’t load {seasonYear} stats.</p>;
  if (!line || n(line.games_played) === 0) {
    return <p className={cn('text-[11px] text-muted-foreground', className)}>No {seasonYear} regular-season games.</p>;
  }

  const conv = conventional(position, line);
  const sab = n(line.sabotage_points);

  return (
    <div className={cn('rounded-lg border border-border bg-muted/10 p-3', className)}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {seasonYear} season
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">{n(line.games_played)} G</span>
      </div>

      {/* Trash line first -- it is what the league is scored on. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
        <Stat label="SPTS" value={(sab >= 0 ? '+' : '') + sab.toFixed(1)}
              tone={sab > 0 ? 'damage' : sab < 0 ? 'good' : 'muted'} />
        <Stat label="AVG" value={(n(line.avg_sabotage) >= 0 ? '+' : '') + n(line.avg_sabotage).toFixed(1)} />
        <Stat label="TRS" value={'+' + n(line.trash_points).toFixed(1)} />
        <Stat label="ANT" value={n(line.anti_points).toFixed(1)} />
      </div>

      {conv.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 border-t border-border/50">
          {conv.map(([label, value]) => <Stat key={label} label={label} value={value} />)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'damage' | 'good' | 'muted' }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={cn(
        'text-xs font-bold tabular-nums',
        tone === 'damage' && 'text-damage',
        tone === 'good' && 'text-emerald-400',
        tone === 'muted' && 'text-muted-foreground',
      )}>{value}</span>
    </div>
  );
}
